import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { addMedication } from '@/lib/health-firestore';
import type { Medication, MedicationTiming } from '@/types';

interface Props {
  babyId: string;
  fromVisitId?: string;
  initialValues?: Partial<Omit<Medication, 'id'>>;
  onSaved: (med: Medication) => void;
  onClose: () => void;
}

const TIMING_OPTIONS: { value: MedicationTiming; label: string }[] = [
  { value: 'morning',          label: 'Morning' },
  { value: 'after_breakfast',  label: 'After Breakfast' },
  { value: 'afternoon',        label: 'Afternoon' },
  { value: 'after_lunch',      label: 'After Lunch' },
  { value: 'before_lunch',     label: 'Before Lunch' },
  { value: 'evening',          label: 'Evening' },
  { value: 'after_dinner',     label: 'After Dinner' },
  { value: 'before_dinner',    label: 'Before Dinner' },
  { value: 'night',            label: 'Night' },
  { value: 'before_breakfast', label: 'Before Breakfast' },
];

const AddMedicineModal = ({ babyId, fromVisitId, initialValues, onSaved, onClose }: Props) => {
  const today = format(new Date(), 'yyyy-MM-dd');

  const [form, setForm] = useState({
    name:            initialValues?.name ?? '',
    dose:            initialValues?.dose ?? '',
    unit:            (initialValues?.unit ?? 'ml') as Medication['unit'],
    foodInstruction: (initialValues?.foodInstruction ?? 'after') as Medication['foodInstruction'],
    startDate:       initialValues?.startDate ?? today,
    endDate:         initialValues?.endDate ?? '',
    timings:         (initialValues?.timings ?? []) as MedicationTiming[],
  });
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const toggleTiming = (t: MedicationTiming) =>
    setForm(prev => ({
      ...prev,
      timings: prev.timings.includes(t)
        ? prev.timings.filter(x => x !== t)
        : [...prev.timings, t],
    }));

  const handleSave = async () => {
    setAttempted(true);
    setSaveError(null);
    if (!form.name || !form.dose || form.timings.length === 0) return;
    setSaving(true);
    try {
      const med = await addMedication(babyId, {
        name:            form.name,
        dose:            form.dose,
        unit:            form.unit,
        frequency:       form.timings.length === 1 ? 'once' : form.timings.length === 2 ? 'twice' : 'thrice',
        timings:         form.timings,
        foodInstruction: form.foodInstruction,
        startDate:       form.startDate,
        ...(form.endDate ? { endDate: form.endDate } : {}),
        isActive:        true,
        ...(fromVisitId ? { fromVisitId } : {}),
      });
      onSaved(med);
    } catch (err) {
      console.error('addMedication failed:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] bg-black/50 flex items-end">
      <div className="w-full max-w-lg mx-auto bg-background rounded-t-2xl max-h-[92vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
          <h2 className="text-base font-semibold">Add Medicine</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Medicine Name *</label>
            <input
              type="text"
              value={form.name}
              placeholder="e.g. Calpol, Vitamin D drops"
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="input-field w-full"
            />
          </div>

          {/* Dose + Unit */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Dose *</label>
              <input
                type="text"
                value={form.dose}
                placeholder="e.g. 5"
                onChange={e => setForm(p => ({ ...p, dose: e.target.value }))}
                className="input-field w-full"
              />
            </div>
            <div className="w-28">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Unit</label>
              <select
                value={form.unit}
                onChange={e => setForm(p => ({ ...p, unit: e.target.value as Medication['unit'] }))}
                className="input-field w-full"
              >
                <option value="ml">ml</option>
                <option value="mg">mg</option>
                <option value="drops">drops</option>
                <option value="tablets">tablets</option>
                <option value="other">other</option>
              </select>
            </div>
          </div>

          {/* Timings */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              When to Give *
              {attempted && form.timings.length === 0 && (
                <span className="ml-2 font-normal" style={{ color: 'hsl(var(--destructive))' }}>— please select at least one</span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {TIMING_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleTiming(value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.timings.includes(value)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Food instruction */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">With Food</label>
            <div className="grid grid-cols-4 gap-2">
              {(['before', 'after', 'with', 'na'] as const).map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, foodInstruction: v }))}
                  className={`py-2 rounded-xl text-xs font-medium border transition-colors ${
                    form.foodInstruction === v
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border'
                  }`}
                >
                  {v === 'na' ? 'N/A' : v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Start Date</label>
            <input
              type="date"
              value={form.startDate}
              onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">End Date <span className="text-muted-foreground font-normal">(optional — leave blank for ongoing)</span></label>
            <input
              type="date"
              value={form.endDate}
              min={form.startDate}
              onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
              className="input-field w-full"
            />
          </div>
        </div>

        {/* Fixed save button */}
        <div className="px-4 pt-3 pb-8 border-t border-border/30 flex-shrink-0 bg-background">
          {saveError && (
            <p className="text-xs mb-2 text-center" style={{ color: 'hsl(var(--destructive))' }}>{saveError}</p>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-primary text-primary-foreground text-sm font-semibold py-3.5 rounded-xl disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Medicine
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMedicineModal;
